import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const LocationContext = createContext(null);

// localStorage key + shape:
//   { source: 'gps' | 'manual' | 'denied',
//     latitude, longitude, city, uf, savedAt }
const STORAGE_KEY = 'user_location_pref';

function readStoredPref() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== 'object') return null;
    return obj;
  } catch (e) {
    return null;
  }
}

function writeStoredPref(pref) {
  try {
    if (pref) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...pref, savedAt: Date.now() }));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (e) {
    // modo privado / storage cheio → ignora silenciosamente
  }
}

async function syncToBackend(pref) {
  // Só envia ao backend se houver token (usuário logado). Falha silenciosa.
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (!token) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/users/me/location-pref`;
    await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(pref || {}),
    });
  } catch (e) {
    // ignora — preferência local já foi salva
  }
}

export const LocationProvider = ({ children }) => {
  const stored = readStoredPref();
  const [location, setLocation] = useState(
    stored && stored.latitude && stored.longitude
      ? { latitude: stored.latitude, longitude: stored.longitude }
      : null,
  );
  const [locationName, setLocationName] = useState(
    stored?.city ? `${stored.city}${stored.uf ? `, ${stored.uf}` : ''}` : 'Perto de você',
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Estado da preferência (gps/manual/denied/null=ainda não escolheu)
  const [pref, setPref] = useState(stored);
  // Flag que controla a exibição do banner não-intrusivo (LocationPrompt)
  const [showPrompt, setShowPrompt] = useState(false);

  /**
   * Pergunta a permissão de geolocalização ao navegador e persiste a decisão.
   * Após GPS bem-sucedido, faz reverse-geocoding contra o catálogo de
   * municípios para identificar a cidade automaticamente.
   */
  const requestLocation = useCallback(() => {
    setLoading(true);
    setError(null);

    if (!navigator.geolocation) {
      const denied = { source: 'denied' };
      setError('Geolocalização não suportada');
      setLoading(false);
      setPref(denied);
      writeStoredPref(denied);
      syncToBackend(denied);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        setLocation(coords);
        setLoading(false);

        // Reverse-geocoding contra o catálogo de municípios cadastrados.
        // Funciona offline em relação a APIs externas (Google/Mapbox).
        let city = null;
        let uf = null;
        try {
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/municipios/nearest?lat=${coords.latitude}&lng=${coords.longitude}&max_km=80`;
          const res = await fetch(url);
          if (res.ok) {
            const m = await res.json();
            city = m?.nome || null;
            uf = m?.uf || null;
            if (city) {
              setLocationName(`${city}${uf ? `, ${uf}` : ''}`);
            }
          }
        } catch (e) {
          // Sem conexão / município não cadastrado — mantém "Perto de você"
        }

        const newPref = {
          source: 'gps',
          ...coords,
          city,
          uf,
        };
        setPref(newPref);
        writeStoredPref(newPref);
        syncToBackend(newPref);
      },
      (err) => {
        console.warn('Geolocalização negada/falhou:', err.message);
        setError(err.message);
        setLoading(false);
        // Default: centro de São Paulo (mantido para compatibilidade
        // com páginas públicas que esperam sempre ter `location`)
        setLocation({ latitude: -23.5505, longitude: -46.6333 });
        const denied = { source: 'denied' };
        setPref(denied);
        writeStoredPref(denied);
        syncToBackend(denied);
      },
      { timeout: 10000, enableHighAccuracy: false },
    );
  }, []);

  /**
   * Define cidade manualmente (sem GPS).
   * `payload`: { city, uf, latitude?, longitude? }
   */
  const setManualLocation = useCallback((payload) => {
    if (!payload || !payload.city) return;
    const newPref = {
      source: 'manual',
      city: payload.city,
      uf: payload.uf || null,
      latitude: payload.latitude ?? null,
      longitude: payload.longitude ?? null,
    };
    if (payload.latitude && payload.longitude) {
      setLocation({ latitude: payload.latitude, longitude: payload.longitude });
    }
    setLocationName(`${payload.city}${payload.uf ? `, ${payload.uf}` : ''}`);
    setPref(newPref);
    setShowPrompt(false);
    writeStoredPref(newPref);
    syncToBackend(newPref);
  }, []);

  /**
   * Dispensa o prompt e marca como "negado" (não pergunta mais sozinho).
   * Usuário pode reativar manualmente via Header → ícone de localização.
   */
  const denyLocation = useCallback(() => {
    const denied = { source: 'denied' };
    setPref(denied);
    setShowPrompt(false);
    writeStoredPref(denied);
    syncToBackend(denied);
  }, []);

  /**
   * Mostra o LocationPrompt (não-intrusivo). Componente é renderizado pelo
   * provider raiz; páginas só chamam essa função quando relevante.
   */
  const openPrompt = useCallback(() => setShowPrompt(true), []);
  const closePrompt = useCallback(() => setShowPrompt(false), []);

  // Auto-prompt: se não há preferência ainda E estamos em página pública,
  // mostra o banner depois de 5s para não ser intrusivo (dá tempo do usuário
  // ler a home antes).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.pathname.startsWith('/admin')) return;
    if (pref) return; // já tem preferência salva
    const t = setTimeout(() => setShowPrompt(true), 5000);
    return () => clearTimeout(t);
  }, [pref]);

  // Sync 1x da preferência salva no backend (usuários logados em outro
  // dispositivo trazem a preferência junto). Roda apenas se há token mas
  // ainda não temos preferência local.
  useEffect(() => {
    if (pref) return;
    const token = (typeof localStorage !== 'undefined')
      ? (localStorage.getItem('token') || localStorage.getItem('access_token'))
      : null;
    if (!token) return;

    let cancelled = false;
    (async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/users/me/location-pref`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        const remote = data?.location_pref;
        if (cancelled || !remote || !remote.source) return;
        setPref(remote);
        writeStoredPref(remote);
        if (remote.latitude && remote.longitude) {
          setLocation({ latitude: remote.latitude, longitude: remote.longitude });
        }
        if (remote.city) {
          setLocationName(`${remote.city}${remote.uf ? `, ${remote.uf}` : ''}`);
        }
        setShowPrompt(false);
      } catch (e) {
        // ignore — preferência local prevalece
      }
    })();
    return () => { cancelled = true; };
  }, [pref]);

  return (
    <LocationContext.Provider
      value={{
        location,
        locationName,
        setLocationName,
        loading,
        error,
        pref,
        requestLocation,
        setManualLocation,
        denyLocation,
        showPrompt,
        openPrompt,
        closePrompt,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within LocationProvider');
  }
  return context;
};
